// src/screens/Client/support/ClientTicketDrawer.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import MyContext from "../../MyContext";
import { toast } from "react-toastify";
import {
  Hash,
  MessageSquareText,
  Building2,
  Flag,
  CalendarClock,
  Clock3,
  CheckCircle2,
  SendHorizonal,
} from "lucide-react";
import { StatusBadge, PriorityBadge } from "./TicketBadges";
import "./client-ticket.css";

const ClientTicketDrawer = ({ open, onClose, ticket, userID }) => {
  const { baseUrl, config } = useContext(MyContext);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [input, setInput] = useState("");
  const [status, setStatus] = useState(ticket?.status || "O");
  const listRef = useRef(null);

  const ticketId = ticket?.pk_ticket_id;

  useEffect(() => {
    setStatus(ticket?.status || "O");
  }, [ticket]);

  const loadConversation = async () => {
    if (!ticketId) return;
    setMsgLoading(true);
    try {
      const body = JSON.stringify({ ticketid: String(ticketId) });
      const res = await fetch(`${baseUrl}/api/Support/v1.0/getSupportTicketConversation`, {
        method: "POST",
        headers: { ...config.headers, "Content-Type": "application/json" },
        body,
      });
      const data = await res.json();
      if (data?.responseCode === "00") {
        setMessages(data?.response || []);
      } else {
        setMessages([]);
        toast.error(data?.responseMessage || "Failed to load chat");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error loading chat");
      setMessages([]);
    } finally {
      setMsgLoading(false);
      setTimeout(() => listRef.current?.scrollTo({ top: 999999, behavior: "smooth" }), 50);
    }
  };

  useEffect(() => {
    if (open && ticketId) loadConversation();
  }, [open, ticketId]);

  const send = async () => {
    const text = input.trim();
    if (!text || !ticketId) return;
    try {
      const body = JSON.stringify({
        ticketid: String(ticketId),
        userid: userID || "",
        message: text,
         sender: "Client"
      });
      const res = await fetch(`${baseUrl}/api/Support/v1.0/insertSupportTicketConversation`, {
        method: "POST",
        headers: { ...config.headers, "Content-Type": "application/json" },
        body,
      });
      const data = await res.json();
      if (data?.responseCode === "00") {
        setInput("");
        loadConversation();
      } else {
        toast.error(data?.responseMessage || "Failed to send");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error sending");
    }
  };

  const metaItems = useMemo(() => {
    if (!ticket) return [];
    return [
      { key: "Ticket Code", val: ticket.ticket_code || "-", icon: Hash, tone: "blue" },
      { key: "Subject", val: ticket.subject || "-", icon: MessageSquareText, tone: "violet" },
      { key: "Department", val: ticket.department || "-", icon: Building2, tone: "teal" },
      { key: "Priority", val: <PriorityBadge value={ticket.priority} />, icon: Flag, tone: "amber" },
      { key: "Created", val: ticket.created_on || "-", icon: CalendarClock, tone: "rose" },
      { key: "Updated", val: ticket.updated_on || "-", icon: Clock3, tone: "indigo" },
      { key: "Closed On", val: ticket.closed_on || "-", icon: CheckCircle2, tone: "green" },
    ];
  }, [ticket]);

  const cls = `offcanvas offcanvas-end ${open ? "show" : ""} client-ticket-drawer`;
  const style = { visibility: open ? "visible" : "hidden" };
// Helper: message is from Client
const isClientMsg = React.useCallback((m) => {
  if (m && typeof m.sender === "string") {
    return m.sender.toLowerCase() === "client";
  }
  // fallback if 'sender' missing
  return !!m?.fk_user_id;
}, []);


  return (
    <div className={cls} style={style} tabIndex="-1">
      <div className="offcanvas-header">
        <div>
          <h5 className="mb-1">
            Ticket #{ticket?.ticket_code} <StatusBadge code={status} className="ms-2" />
          </h5>
          <div className="small text-muted">{ticket?.subject}</div>
        </div>
        <button className="btn-close" onClick={() => onClose(false)} />
      </div>

      <div className="offcanvas-body d-flex flex-column">
        {/* meta */}
        <div className="client-ticket-meta mb-3">
          <div className="client-ticket-meta-grid">
            {metaItems.map((m) => {
              const Icon = m.icon;
              return (
                <div key={m.key} className={`client-ticket-meta-item client-ticket-tone-${m.tone}`}>
                  <div className="client-ticket-meta-icon">
                    <Icon size={18} />
                  </div>
                  <div className="client-ticket-meta-content">
                    <div className="client-ticket-meta-label">{m.key}</div>
                    <div className="client-ticket-meta-value">{m.val}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* chat */}
        <div className="card flex-grow-1 mb-3 client-ticket-chat w-100">
          <div className="card-header d-flex align-items-center justify-content-between">
            <div className="fw-semibold">Chat with Support</div>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={loadConversation}
              disabled={msgLoading}
            >
              {msgLoading ? "Loading..." : "Refresh"}
            </button>
          </div>
        <div className="card-body client-ticket-chat-body" ref={listRef}>
  {msgLoading ? (
    <div className="text-center py-4">
      <div className="spinner-border" role="status" />
    </div>
  ) : Array.isArray(messages) && messages.length > 0 ? (
    messages.map((m, i) => {
      const clientSide = isClientMsg(m);
      return (
        <div
          key={m?.pk_conversation_id ?? i}
          className={`client-ticket-msg ${clientSide ? "is-client" : "is-support"}`}
        >
          <div className="client-ticket-avatar">{clientSide ? "You" : "S"}</div>
          <div className="client-ticket-bubble">
            {/* {m?.update_type ? (
              <div className="client-ticket-msg-update chip">{m.update_type}</div>
            ) : null} */}
            <div className="client-ticket-text">{m?.message ?? ""}</div>
            <div className="client-ticket-meta small text-muted">
              {m?.created_on ?? ""}
            </div>
          </div>
        </div>
      );
    })
  ) : (
    <div className="text-center text-muted">No messages yet.</div>
  )}
</div>

          <div className="card-footer">
            <div className="input-group">
              <input
                className="form-control"
                placeholder="Write a message…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
              />
              <button className="btn btn-success d-inline-flex align-items-center gap-2" onClick={send}>
                <SendHorizonal size={16} />
                Send
              </button>
            </div>
          </div>
        </div>

        <div className="d-grid">
          <button className="btn btn-outline-secondary" onClick={() => onClose(false)}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientTicketDrawer;
